import React, { FC, useContext, useEffect, useState } from 'react'

import { Box, Checkbox, FormControlLabel, FormGroup, Grid, Paper, Typography } from '@mui/material'
import { DataContext } from '../../contexts/DataProvider'
import { getChartDate } from '../../helpers/getChartDate'
import { GRAPH_COLOR_1, GRAPH_COLOR_2, GRAPH_COLOR_3 } from '../../utils/colors'
import Title from '../Title'
import ChartTests from './ChartTests'
import { TestsChart } from '../../utils/models'
import TestInfoCard from './TestsInfoCard'
import useFetch from '../../hooks/useFetch'
import { getPeriod } from '../../helpers/helpers'
import ChartLoader from '../ChartLoader'

interface Data {
  time: string
  sdmt?: number
  walking?: number
  questions?: number
}
export interface Counter {
  questionsScheduled: number
  questionsCompleted: number
  walkingScheduled: number
  walkingCompleted: number
  sdmtScheduled: number
  sdmtCompleted: number
}

const tests = {
  walkingScheduled: {
    checked: true,
    type: 'walking',
    name: '2MWT Scheduled',
    value: 'walkingScheduled',
    variant: 'scheduledTests',
    color: GRAPH_COLOR_2,
  },
  walkingCompleted: {
    checked: true,
    type: 'walking',
    name: '2MWT completed',
    value: 'walkingCompleted',
    variant: 'completedTests',
    color: GRAPH_COLOR_2,
  },
  questionsScheduled: {
    checked: false,
    type: 'questions',
    name: 'Questions Scheduled',
    value: 'questionsScheduled',
    variant: 'scheduledTests',
    color: GRAPH_COLOR_1,
  },
  questionsCompleted: {
    checked: false,
    type: 'questions',
    name: 'Questions completed',
    value: 'questionsCompleted',
    variant: 'completedTests',
    color: GRAPH_COLOR_1,
  },
  sdmtScheduled: {
    checked: false,
    type: 'sdmt',
    name: 'SDMT Scheduled',
    value: 'sdmtScheduled',
    variant: 'scheduledTests',
    color: GRAPH_COLOR_3,
  },
  sdmtCompleted: {
    checked: false,
    type: 'sdmt',
    name: 'SDMT completed',
    value: 'sdmtCompleted',
    variant: 'completedTests',
    color: GRAPH_COLOR_3,
  },
}
const counter = {
  questionsScheduled: 0,
  questionsCompleted: 0,
  walkingScheduled: 0,
  walkingCompleted: 0,
  sdmtScheduled: 0,
  sdmtCompleted: 0,
}
const ContractTests: FC = () => {
  const { period, activeContract, startDate, endDate } = useContext(DataContext)
  const [checkboxes, setCheckboxes] = useState(tests)
  const [chartResult, setChartResult] = useState<Data[]>([])
  const [testsCounter, setTestsCounter] = useState(counter)

  const [isLoadingSdmt, dataSdmt, errorSdmt] = useFetch(
    'getScheduledExperiments',
    { method: 'GET' },
    [activeContract, startDate, endDate],
    {
      'studyIds[]': activeContract?.id,
      startTime: startDate.format('MM.DD.YYYY'),
      endTime: endDate.format('MM.DD.YYYY'),
      period: getPeriod(period),
      type: 'sdmt',
      noLimits: true,
    },
  )
  const [isLoadingWalking, dataWalking, errorWalking] = useFetch(
    'getScheduledExperiments',
    { method: 'GET' },
    [activeContract, startDate, endDate],
    {
      'studyIds[]': activeContract?.id,
      startTime: startDate.format('MM.DD.YYYY'),
      endTime: endDate.format('MM.DD.YYYY'),
      period: getPeriod(period),
      type: 'walking',
      noLimits: true,
    },
  )
  const [isLoadingObservations, dataObservations, errorObservations] = useFetch(
    'getScheduledObservations',
    { method: 'GET' },
    [activeContract, startDate, endDate],
    {
      'studyIds[]': activeContract?.id,
      startTime: startDate.format('MM.DD.YYYY'),
      endTime: endDate.format('MM.DD.YYYY'),
      period: getPeriod(period),
      type: 'pro',
      noLimits: true,
    },
  )

  const handleChange = (event) => {
    setCheckboxes({
      ...checkboxes,
      [event.target.name]: { ...checkboxes[event.target.name], checked: event.target.checked },
    })
  }

  let dataWrapper = {
    walking: [],
    sdmt: [],
    questions: [],
  }

  useEffect(() => {
    if (!dataWalking || !dataSdmt || !dataObservations) {
      return
    }
    dataWrapper = {
      walking: dataWalking?.data,
      sdmt: dataSdmt?.data,
      questions: dataObservations?.data,
    }

    const getSum = (object, type: 'scheduledTests' | 'completedTests') =>
      object?.data.map((el) => el[type])?.reduce((acc, curr) => acc + curr, 0)

    const questionsScheduled: number = getSum(dataObservations, 'scheduledTests')

    const questionsCompleted: number = getSum(dataObservations, 'completedTests')

    const walkingScheduled: number = getSum(dataWalking, 'scheduledTests')
    const walkingCompleted: number = getSum(dataWalking, 'completedTests')
    const sdmtScheduled: number = getSum(dataSdmt, 'scheduledTests')
    const sdmtCompleted: number = getSum(dataSdmt, 'completedTests')

    setTestsCounter({
      questionsScheduled,
      questionsCompleted,
      walkingScheduled,
      walkingCompleted,
      sdmtScheduled,
      sdmtCompleted,
    })

    const activeTests = Object.values(checkboxes).filter((test) => test.checked)
    const merged: Data[] = []

    activeTests.map((test) => {
      dataWrapper[test.type].map((contract: TestsChart) => {
        const time = getChartDate(period, contract)
        const dateIndex = merged.findIndex((date: Data) => date.time === time)

        if (dateIndex >= 0) {
          merged[dateIndex][test.value] = contract[test.variant]
        } else {
          merged.push({
            time,
            [test.value]: contract[test.variant],
          })
        }
      })
    })

    setChartResult(merged)
  }, [checkboxes, dataSdmt, dataObservations, dataWalking])

  return (
    <>
      <Grid item xs={12} md={9} lg={9}>
        <Paper
          sx={{
            p: 2,
            mb: 3,
          }}
        >
          <Title>Choose tests:</Title>
          {Object.values(checkboxes).map(({ checked, value, name }) => (
            <FormControlLabel
              key={value}
              control={<Checkbox checked={checked} onChange={handleChange} name={value} />}
              label={name}
            />
          ))}
        </Paper>
        <Paper
          sx={{
            p: 2,
            position: 'relative',
          }}
        >
          <ChartTests results={chartResult} testTypes={checkboxes} title="Tests:" />

          {isLoadingSdmt || isLoadingWalking || isLoadingObservations ? <ChartLoader /> : null}
        </Paper>
      </Grid>
      <TestInfoCard counter={testsCounter} />
    </>
  )
}
export default ContractTests
